<script lang="ts" setup>
  import { ref, toRefs } from 'vue'
  import { TShapeProps } from '../store/shapes'
  import { KonvaEventObject } from 'konva/lib/Node'
  import ActionCircles from './ActionCircles.vue'
  import Konva from 'konva'
  import ActionOptions from './ActionOptions.vue'
  const props = defineProps<TShapeProps>()
  const shapeRef = ref<Konva.LineConfig>()
  const { shape, image, device, draggable } = toRefs(props)
  const emits = defineEmits(['ondragstart', 'ondragmoving', 'ondragstop', 'onselectshape', 'ondelete', 'oncopy'])
  const handleSelect = () => {
    emits('onselectshape', shape.value)
  }
  const handleDragStart = () => {
    emits('ondragstart', shape.value)
  }
  const handleDragMove = (e: KonvaEventObject<Event>) => {
    shape.value.x = e?.target?.x() / image.value?.scale
    shape.value.y = e?.target?.y() / image.value?.scale
    emits('ondragmoving', shape.value)
  }
  const handleDragStop = () => {
    emits('ondragstop', shape.value)
  }

  const handleDragMovePoint = (circlePosition: number, x: number, y: number) => {
    if (circlePosition === 0) {
      shape.value.x = x / image.value.scale
      shape.value.y = y / image.value.scale
      return
    }
    if (circlePosition === 2) {
      const dx = x / image.value.scale - shape.value.x
      const dy = y / image.value.scale - shape.value.y
      const newRadius = Math.sqrt(dx * dx + dy * dy)

      const newPoints = shape.value.points
      newPoints[circlePosition] = dx
      newPoints[circlePosition + 1] = dy
      shape.value.points = newPoints
      shape.value.radius = newRadius
    }
  }
</script>

<template>
  <v-circle
    :id="`circle-shadow-${shape?.id}`"
    ref="shapeRef"
    :draggable="false"
    :config="{
      x: shape?.x * image?.scale + device.shadow_range,
      y: shape?.y * image?.scale + device.shadow_range,
      fill: 'transparent',
      stroke: 'black',
      strokeWidth: device.thickness,
      rotation: 0,
      radius: shape?.radius * image.scale,
      hitStrokeWidth: 40,
      fillEnabled: false,
    }"
  ></v-circle>
  <v-circle
    :id="shape?.id"
    ref="shapeRef"
    :draggable="draggable"
    :config="{
      x: shape?.x * image?.scale,
      y: shape?.y * image?.scale,
      fill: 'transparent',
      stroke: shape?.color,
      fillEnabled: false,
      strokeWidth: device.thickness,
      rotation: 0,
      hitStrokeWidth: 40,
      radius: shape?.radius * image.scale,
    }"
    @click="handleSelect"
    @tap="handleSelect"
    @touchstart="handleDragStart"
    @touchmove="handleDragMove"
    @touchend="handleDragStop"
    @dragstart="handleDragStart"
    @dragmove="handleDragMove"
    @dragend="handleDragStop"
  ></v-circle>
  <ActionOptions
    :options="['copy', 'delete']"
    :show="isSelected"
    :x="shape.x * image.scale - 70"
    :y="shape.y * image.scale - 80"
    @delete="() => emits('ondelete', shape)"
    @copy="() => emits('oncopy', shape)"
  />
  <ActionCircles
    v-if="isSelected"
    :id="shape?.id"
    :color="shape.color"
    :keyshape="'circle'"
    :positions="[0, 2]"
    :points="() => [shape.points[0] * image.scale, shape.points[1] * image.scale, shape.points[2] * image.scale, shape.points[3] * image.scale]"
    :xs="shape.x * image.scale"
    :ys="shape.y * image.scale"
    @ondragmoving="({ circlePoint, x, y }) => handleDragMovePoint(circlePoint, x, y)"
    @ondragstop="handleDragStop"
  />
</template>
